import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { ProformaStatusFilter, ProformaViewMode } from "../proforma.constants";
import { ProformaViewToggle } from "./ProformaViewToggle";

interface ProformasToolbarProps {
  search: string;
  statusFilter: ProformaStatusFilter;
  viewMode: ProformaViewMode;
  filteredCount: number;
  totalCount: number;
  onSearchChange: (value: string) => void;
  onStatusFilterChange: (value: ProformaStatusFilter) => void;
  onViewModeChange: (mode: ProformaViewMode) => void;
  onResetFilters: () => void;
}

export function ProformasToolbar({
  search,
  statusFilter,
  viewMode,
  filteredCount,
  totalCount,
  onSearchChange,
  onStatusFilterChange,
  onViewModeChange,
  onResetFilters,
}: ProformasToolbarProps) {
  const hasFilters = search || statusFilter !== "all";

  return (
    <div className="mb-4 grid gap-3 border-y border-border py-3 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-center">
      <div className="grid min-w-0 gap-2 md:grid-cols-[minmax(240px,420px)_180px_auto]">
        <div className="relative min-w-0">
          <Search
            size={14}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-subtle"
          />
          <Input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Rechercher numéro, client, contact, ligne..."
            className="pl-9"
          />
        </div>

        <Select
          value={statusFilter}
          onValueChange={(value) => onStatusFilterChange(value as ProformaStatusFilter)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="draft">Valides</SelectItem>
            <SelectItem value="issued">Émises</SelectItem>
            <SelectItem value="expired">Expirées</SelectItem>
            <SelectItem value="cancelled">Annulées</SelectItem>
          </SelectContent>
        </Select>

        {hasFilters && (
          <Button type="button" variant="secondary" size="sm" onClick={onResetFilters}>
            Réinitialiser
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between xl:justify-end">
        <p className="text-[11.5px] text-muted-foreground">
          {filteredCount} résultat{filteredCount !== 1 ? "s" : ""}
          {filteredCount !== totalCount ? ` sur ${totalCount}` : ""}
        </p>
        <ProformaViewToggle viewMode={viewMode} onViewModeChange={onViewModeChange} />
      </div>
    </div>
  );
}
